import { authenticatedInstance } from '../index/index.service';
import type {
  ChargeManualCreditRequest,
  ChargeTapRequest,
  CreditPaymentStatusView,
  PaymentTerminalView,
} from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';

export type { CreditPaymentStatusView, PaymentTerminalView } from 'shared-models/browser';

export type ReconcileExternalCreditRequest =
  | {
      rangeType: 'date';
      fromDate: string;
      fromTime: string;
      toDate: string;
      toTime: string;
      matchWindowMinutes?: number;
    }
  | {
      rangeType: 'session';
      sessionId: number;
      matchWindowMinutes?: number;
    }
  | {
      rangeType: 'recent';
      lookbackHours?: number;
      matchWindowMinutes?: number;
    };

export type ReconcileExternalCreditResponse = {
  range: {
    fromDate: string;
    toDate: string;
    startInclusive: string;
    endExclusive: string;
  };
  summary: {
    localCount: number;
    externalCount: number;
    matchedCount: number;
    missingInExternalCount: number;
    missingInLocalCount: number;
  };
  matches: Array<{
    rule: string;
    amountDelta: number | null;
    timeDeltaMinutes: number | null;
    local: {
      transactionId: number;
      paymentlineId: number;
      orderId: number;
      amount: number;
      createdAt: string;
      referenceNumber: string | null;
      approvalNumber: string | null;
      rrn: string | null;
      cardLast4: string | null;
    };
    external: {
      transactionId: string | null;
      voucherNumber: string | null;
      approvalNumber: string | null;
      rrn: string | null;
      cardLast4: string | null;
      amount: number | null;
      occurredAt: string | null;
      status: string;
      statusText: string | null;
    };
  }>;
  missingInExternal: Array<{
    transactionId: number;
    paymentlineId: number;
    orderId: number;
    amount: number;
    createdAt: string;
    referenceNumber: string | null;
    approvalNumber: string | null;
    rrn: string | null;
    cardLast4: string | null;
  }>;
  missingInLocal: Array<{
    transactionId: string | null;
    voucherNumber: string | null;
    terminalNumber: string | null;
    approvalNumber: string | null;
    rrn: string | null;
    cardLast4: string | null;
    cardBrand: string | null;
    statusText: string | null;
    status: string;
    amount: number | null;
    occurredAt: string | null;
  }>;
  diagnostics: { source: 'api'; reportedRows: number; eligibleRows: number };
};

class CreditPaymentsService {
  // Summary: Lists active payment terminals (with active pinpads, no secret) for the POS payment-method picker.
  // Details: docs/function-library/web/services.md#credit-payments-service
  async listTerminals(tenantId: string, employeeSession: string): Promise<PaymentTerminalView[]> {
    try {
      const response = await authenticatedInstance.get<PaymentTerminalView[]>(
        `/payments/tenant/${tenantId}/credit/terminals`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Charges a 'new' paymentline on the chosen pinpad; backend derives the amount, promotes the line to credit, and auto-settles+closes on approval.
  // Details: docs/function-library/web/services.md#credit-payments-service
  async charge(
    tenantId: string,
    paymentlineId: number,
    payload: { terminalId: number; pinpadId: string },
    employeeSession: string,
  ): Promise<CreditPaymentStatusView> {
    try {
      const response = await authenticatedInstance.post<CreditPaymentStatusView>(
        `/payments/tenant/${tenantId}/credit/${paymentlineId}/charge`,
        payload,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Charges a 'new' paymentline by manual card entry (card-not-present); card data is request-only and never persisted; shares the pinpad settle+SSE spine.
  // Details: docs/function-library/web/services.md#credit-payments-service
  async chargeManual(
    tenantId: string,
    paymentlineId: number,
    payload: ChargeManualCreditRequest,
    employeeSession: string,
  ): Promise<CreditPaymentStatusView> {
    try {
      const response = await authenticatedInstance.post<CreditPaymentStatusView>(
        `/payments/tenant/${tenantId}/credit/${paymentlineId}/charge-manual`,
        payload,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  async startTapBridge(tenantId: string, paymentlineId: number, employeeSession: string): Promise<{ amount: number }> {
    try {
      const response = await authenticatedInstance.post<{ amount: number }>(
        `/payments/tenant/${tenantId}/credit/${paymentlineId}/tap-bridge/start`,
        {},
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  async releaseTapBridge(
    tenantId: string,
    paymentlineId: number,
    employeeSession: string,
    payload: {
      bridgeOutcome?: 'declined' | 'cancelled';
      bridgeMessage?: string | null;
      rawPayload?: unknown;
      diagnostics?: unknown[];
    } = {},
  ): Promise<void> {
    try {
      await authenticatedInstance.post(
        `/payments/tenant/${tenantId}/credit/${paymentlineId}/tap-bridge/release`,
        payload,
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Records a device-driven tap-on-phone (TOP) witness on a 'new' paymentline; the backend verifies it against Z-Credit by reference before settling, sharing the credit settle+SSE spine.
  // Details: docs/function-library/web/services.md#credit-payments-service
  async chargeTap(
    tenantId: string,
    paymentlineId: number,
    payload: ChargeTapRequest & {
      bridgeOutcome?: string;
      bridgeMessage?: string | null;
      rawPayload?: unknown;
      diagnostics?: unknown[];
    },
    employeeSession: string,
  ): Promise<CreditPaymentStatusView> {
    try {
      const response = await authenticatedInstance.post<CreditPaymentStatusView>(
        `/payments/tenant/${tenantId}/credit/${paymentlineId}/charge-tap`,
        payload,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Durable recovery read of the latest attempt + paymentline status (no gateway call) for a stuck/unknown charge.
  // Details: docs/function-library/web/services.md#credit-payments-service
  async getStatus(
    tenantId: string,
    paymentlineId: number,
    employeeSession: string,
  ): Promise<CreditPaymentStatusView> {
    try {
      const response = await authenticatedInstance.get<CreditPaymentStatusView>(
        `/payments/tenant/${tenantId}/credit/${paymentlineId}/status`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Resolves the latest 'unknown' attempt via gateway status lookup; idempotent (returns current state when nothing is unknown/inconclusive).
  // Details: docs/function-library/web/services.md#credit-payments-service
  async reconcile(
    tenantId: string,
    paymentlineId: number,
    employeeSession: string,
  ): Promise<CreditPaymentStatusView> {
    try {
      const response = await authenticatedInstance.post<CreditPaymentStatusView>(
        `/payments/tenant/${tenantId}/credit/${paymentlineId}/reconcile`,
        {},
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Reconciles local approved credit transactions against the tenant's configured Z-Credit terminal report.
  async reconcileExternal(
    tenantId: string,
    payload: ReconcileExternalCreditRequest,
    employeeSession?: string,
  ): Promise<ReconcileExternalCreditResponse> {
    try {
      const response = await authenticatedInstance.post<ReconcileExternalCreditResponse>(
        `/payments/tenant/${tenantId}/credit/reconcile-external`,
        payload,
        {
          headers: employeeSession
            ? { 'x-employee-session': employeeSession }
            : undefined,
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }
}

export const creditPaymentsService = new CreditPaymentsService();
